import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, ClipboardList, AlertTriangle, Phone, CheckCircle2,
  Download, PenLine, User as UserIcon, RefreshCw,
} from "lucide-react";

/**
 * Página pública do Plano de Cuidados Pontual.
 *
 * Alvo: Ajudante Familiar (afecto ao serviço) e Cliente/Familiar
 * responsável. Ambos abrem o mesmo link partilhado pelo Consultor
 * e podem:
 *   1. Ler o plano completo (só os itens ticados);
 *   2. Descarregar o PDF;
 *   3. Assinar digitalmente no canvas (rúbrica) — a assinatura fica
 *      gravada no plano com o role escolhido e data/hora, aparece na
 *      próxima geração do PDF.
 *
 * URL: `/care-plan/pontual/:token` — token vem de
 * `pontual_care_plan.public_token`, gerado na aprovação do plano.
 */
const API = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso, withTime = false) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString("pt-PT", {
      day: "2-digit", month: "short", year: "numeric",
      ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
    });
  } catch { return iso; }
}

function useSignaturePad() {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    // Scale for retina
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0F172A";
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const evt = e.touches ? e.touches[0] : e;
    return { x: evt.clientX - r.left, y: evt.clientY - r.top };
  };
  const start = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasContent(true);
  };
  const end = () => setDrawing(false);
  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setHasContent(false);
  };
  const toBase64 = () => {
    const c = canvasRef.current;
    if (!c) return "";
    return c.toDataURL("image/png");
  };
  return { canvasRef, start, move, end, clear, toBase64, hasContent };
}

const SECTION_META = {
  activities: {
    icon: ClipboardList, title: "Actividades a executar",
    color: "text-[hsl(var(--success))]", bg: "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/25",
  },
  priorities: {
    icon: AlertTriangle, title: "Pontos de atenção",
    color: "text-[hsl(var(--warning))]", bg: "bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning))]/25",
  },
  escalation: {
    icon: Phone, title: "Quando escalar",
    color: "text-[hsl(var(--danger))]", bg: "bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/25",
  },
};

function Section({ items, sectionKey }) {
  if (!items || !items.length) return null;
  const meta = SECTION_META[sectionKey];
  const Icon = meta.icon;
  return (
    <div className="space-y-2 mb-8" data-testid={`section-${sectionKey}`}>
      <h3 className={`font-display text-sm uppercase tracking-widest flex items-center gap-2 ${meta.color}`}>
        <Icon className="w-4 h-4" /> {meta.title}
      </h3>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className={`flex items-start gap-3 rounded-lg border px-4 py-2.5 ${meta.bg}`}>
            <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${meta.color}`} />
            <span className="text-sm text-foreground">{it.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SignatureCard({ role, roleLabel, existing, onSigned, token }) {
  const [name, setName] = useState(existing?.name || "");
  const [busy, setBusy] = useState(false);
  const pad = useSignaturePad();

  const submit = async () => {
    if (!name.trim()) return toast.error("Escreve o teu nome primeiro");
    if (!pad.hasContent) return toast.error("Assina no espaço antes de submeter");
    setBusy(true);
    try {
      await axios.post(`${API}/api/public/pontual-care-plan/${token}/sign`, {
        name: name.trim(),
        role,
        image_base64: pad.toBase64(),
      });
      toast.success(`Assinatura de ${roleLabel} registada`);
      onSigned && onSigned();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falhou o registo da assinatura");
    } finally {
      setBusy(false);
    }
  };

  if (existing?.signed_at) {
    return (
      <div className="rounded-2xl border border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))] p-5" data-testid={`sig-done-${role}`}>
        <div className="flex items-center gap-2 text-[hsl(var(--success))] mb-1">
          <CheckCircle2 className="w-4 h-4" />
          <span className="font-ui text-sm uppercase tracking-widest">{roleLabel} · Assinado</span>
        </div>
        <div className="text-sm text-[hsl(var(--success))]">
          <b>{existing.name}</b> · {fmtDate(existing.signed_at, true)}
        </div>
        <button
          onClick={() => onSigned && onSigned({ resign: true })}
          className="mt-3 inline-flex items-center gap-1 text-[11px] text-[hsl(var(--success))] hover:text-[hsl(var(--success))]"
          data-testid={`sig-resign-${role}`}
        >
          <RefreshCw className="w-3 h-3" /> Reassinar
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={`sig-form-${role}`}>
      <div className="flex items-center gap-2 text-foreground mb-1">
        <PenLine className="w-4 h-4 text-muted-foreground" />
        <span className="font-ui text-sm uppercase tracking-widest">{roleLabel}</span>
      </div>
      <div className="text-xs text-muted-foreground mb-4">
        Escreve o teu nome e assina no espaço abaixo com o dedo, rato ou stylus.
      </div>
      <div className="mb-3">
        <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Nome</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full mt-1 border border-border rounded-lg px-3 py-2 text-sm"
          placeholder="Nome completo"
          data-testid={`sig-name-${role}`}
        />
      </div>
      <div className="mb-3">
        <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Assinatura</label>
        <canvas
          ref={pad.canvasRef}
          onMouseDown={pad.start} onMouseMove={pad.move} onMouseUp={pad.end} onMouseLeave={pad.end}
          onTouchStart={pad.start} onTouchMove={pad.move} onTouchEnd={pad.end}
          style={{ width: "100%", height: "140px", touchAction: "none" }}
          className="mt-1 border border-border rounded-lg bg-muted/40 cursor-crosshair"
          data-testid={`sig-canvas-${role}`}
        />
      </div>
      <div className="flex items-center justify-between">
        <button
          onClick={pad.clear}
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground underline"
          data-testid={`sig-clear-${role}`}
        >
          Limpar
        </button>
        <button
          onClick={submit}
          disabled={busy}
          className="inline-flex items-center gap-1.5 text-sm bg-white/10 hover:bg-white/10 text-primary-foreground rounded-full px-5 py-2 font-semibold disabled:opacity-50"
          data-testid={`sig-submit-${role}`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PenLine className="w-3.5 h-3.5" />}
          Submeter assinatura
        </button>
      </div>
    </div>
  );
}

export default function PublicPontualCarePlan() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try {
      const r = await axios.get(`${API}/api/public/pontual-care-plan/${token}`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Plano indisponível");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/40 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-muted/40 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-red-500" />
          <div className="text-lg font-ui text-foreground mb-1">Não foi possível abrir o plano</div>
          <div className="text-sm text-muted-foreground">{error}</div>
        </div>
      </div>
    );
  }

  const sigByRole = (r) => (data.signatures || []).find((s) => s.role === r);
  const dateRange = (() => {
    const s = data.service_start_date, e = data.service_end_date;
    if (s && e && s !== e) return `${fmtDate(s)} — ${fmtDate(e)}`;
    return fmtDate(s || e);
  })();

  return (
    <div className="min-h-screen bg-muted/40 pb-20">
      <header className="bg-white/10 text-primary-foreground py-8 px-6 mb-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">
            Caring · Plano de Cuidados
          </div>
          <h1 className="font-display text-3xl lg:text-4xl leading-tight" data-testid="public-cp-title">
            {data.beneficiary_name || "Beneficiário"}
          </h1>
          <div className="text-sm text-muted-foreground/70 mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span><b className="text-primary-foreground">Período:</b> {dateRange}</span>
            {data.contact_name && (
              <span><b className="text-primary-foreground">Contacto:</b> {data.contact_name}</span>
            )}
            {data.approved_by_name && (
              <span><b className="text-primary-foreground">Aprovado por:</b> {data.approved_by_name}</span>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6">
        {/* Objectives */}
        {data.objectives && data.objectives.length > 0 && (
          <div className="mb-8" data-testid="section-objectives">
            <h3 className="font-display text-sm uppercase tracking-widest text-primary mb-2">
              Objectivos do serviço
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-foreground text-[15px]">
              {data.objectives.map((o, i) => <li key={i}>{o}</li>)}
            </ol>
          </div>
        )}

        <Section items={data.activities} sectionKey="activities" />
        <Section items={data.priorities} sectionKey="priorities" />
        <Section items={data.escalation} sectionKey="escalation" />

        {/* Download PDF (with signatures if present) */}
        <div className="mb-10 flex flex-wrap gap-3">
          <a
            href={`${API}/api/public/pontual-care-plan/${token}/pdf`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm bg-card hover:bg-muted border border-border rounded-full px-5 py-2 font-semibold text-foreground"
            data-testid="btn-public-pdf"
          >
            <Download className="w-4 h-4" /> Descarregar PDF
          </a>
        </div>

        {/* Signature area */}
        <div className="mb-6">
          <h2 className="font-display text-2xl text-foreground mb-2 flex items-center gap-2">
            <PenLine className="w-5 h-5 text-primary" /> Assinaturas
          </h2>
          <p className="text-sm text-muted-foreground mb-5">
            O Ajudante Familiar assina após a sessão. O Cliente confirma no fim.
            Ambas as assinaturas ficam no PDF final para efeitos de registo.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <SignatureCard
            role="cuidador"
            roleLabel="Ajudante Familiar"
            existing={sigByRole("cuidador")}
            onSigned={load}
            token={token}
          />
          <SignatureCard
            role="cliente"
            roleLabel="Cliente / Familiar responsável"
            existing={sigByRole("cliente")}
            onSigned={load}
            token={token}
          />
        </div>
      </div>
    </div>
  );
}
